import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import { RoofFramingComponentType } from "building-types/framing/roofFramingComponentTypes";
import { RoofFramingType, type RoofTruss } from "building-types/framing/roofFramingTypes";
import type { StickFrameTrussBasicInput } from "building-types/framing/roofTrussTypes";
import { RoofTrussType } from "building-types/framing/roofTrussTypes";
import type { UnsignedNumber } from "common/types/nominalNumbersTypes";
import { unsignedNumberNew } from "common/utils/nominalNumbers";
import { lumberPiecesByProfileNew } from "doc/utils/lumberTools";
import { planeCopyMoveAlongNormal } from "geom/plane";
import { polyLine3New, polyLine3Reverse, type PolyLine3 } from "geom/polyLine3";
import type { TrussGenerator } from "./trussGenerator";
import {
  generateBottomChord,
  generateTopChords,
  projectChordsToFramingPlane,
  setupBottomChordRails,
  setupTopChordRails,
  setupWebChordRails,
} from "./trussUtils";

export class StickFrameTrussGenerator implements TrussGenerator<StickFrameTrussBasicInput> {
  private generateWebChords(
    leftTopChordPitch: UnsignedNumber,
    rightTopChordPitch: UnsignedNumber,
    bottomChord: PolyLine3,
    webChordHorizontalSpacing: UnsignedNumber,
    webChordHalfWidth: UnsignedNumber,
    topChordWidth: UnsignedNumber,
  ): PolyLine3[] {
    const webChords: PolyLine3[] = [];
    const bottomChordTopZ = 0.5 * (bottomChord[2] + bottomChord[5]);
    const webTopZOffset = topChordWidth;
    const webTopLeftZOffset = topChordWidth * Math.sqrt(1 + leftTopChordPitch * leftTopChordPitch);
    const webTopRightZOffset = topChordWidth * Math.sqrt(1 + rightTopChordPitch * rightTopChordPitch);
    const leftX = -Math.abs(bottomChordTopZ) / leftTopChordPitch - webChordHalfWidth;
    const rightX = Math.abs(bottomChordTopZ) / rightTopChordPitch - webChordHalfWidth;
    webChords.push(polyLine3New([-webChordHalfWidth, 0, -webTopZOffset, -webChordHalfWidth, 0, bottomChordTopZ])); //middle web chord

    let offsetXFromStart: number = webChordHorizontalSpacing;
    let startX = 0;
    let startZ = 0;

    //left side web chords
    while (offsetXFromStart < Math.abs(leftX)) {
      startX = -offsetXFromStart;
      startZ = startX * leftTopChordPitch - webTopLeftZOffset;

      //if Z is less than the bottom chord top Z, we are done
      if (startZ < bottomChordTopZ) {
        break;
      }
      webChords.push(
        polyLine3New([startX - webChordHalfWidth, 0, startZ, startX - webChordHalfWidth, 0, bottomChordTopZ]),
      );
      //this is a rule of thumb to prevent creating close chords // can be a user input
      if (offsetXFromStart + 0.5 * webChordHorizontalSpacing > Math.abs(leftX)) {
        break;
      }
      offsetXFromStart += webChordHorizontalSpacing;
    }

    offsetXFromStart = webChordHorizontalSpacing;

    while (offsetXFromStart < rightX) {
      startX = offsetXFromStart + webChordHalfWidth;
      startZ = -startX * rightTopChordPitch - webTopRightZOffset;

      //if Z is less than the bottom chord top Z, we are done
      if (startZ < bottomChordTopZ) {
        break;
      }

      webChords.push(
        polyLine3New([startX - webChordHalfWidth, 0, startZ, startX - webChordHalfWidth, 0, bottomChordTopZ]),
      );

      //this is a rule of thumb to prevent creating close chords // can be a user input
      if (offsetXFromStart + 0.5 * webChordHorizontalSpacing > Math.abs(rightX)) {
        break;
      }
      offsetXFromStart += webChordHorizontalSpacing;
    }

    return webChords;
  }
  generate(input: StickFrameTrussBasicInput): RoofTruss {
    const {
      leftSpan,
      rightSpan,
      leftOverhang,
      rightOverhang,
      webChordHorizontalSpacing,
      lookoutProfile,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordProfile,
      bottomChordProfile,
      webChordProfile,
      bottomChordOffset,
      leftEaveFasciaSize,
      rightEaveFasciaSize,
      trussPlane,
      additionalPlaneOffset, //this is the offset for the bottom chord, so other chords offset need to be with respect to this
    } = input;

    if (Array.isArray(leftSpan) || Array.isArray(rightSpan)) {
      throw new Error("Stick frame truss does not support multiple spans");
    }
    const bottomChordPlaneWithOffset = planeCopyMoveAlongNormal(trussPlane, additionalPlaneOffset);

    const topChordHeight = unsignedNumberNew(LumberProfileCatalog[topChordProfile].width);
    const topChordWidth = unsignedNumberNew(LumberProfileCatalog[topChordProfile].height);
    const bottomChordWidth = unsignedNumberNew(LumberProfileCatalog[bottomChordProfile].height);
    const bottomChordHeight = unsignedNumberNew(LumberProfileCatalog[bottomChordProfile].width);
    const webChordHeight = unsignedNumberNew(LumberProfileCatalog[webChordProfile].width);
    const webChordHalfWidth = unsignedNumberNew(LumberProfileCatalog[webChordProfile].height / 2);
    const lookoutHeight = unsignedNumberNew(LumberProfileCatalog[lookoutProfile].width);
    const { left, right } = generateTopChords(
      leftSpan,
      rightSpan,
      leftTopChordPitch,
      rightTopChordPitch,
      -lookoutHeight * Math.sqrt(1 + leftTopChordPitch * leftTopChordPitch),
      -lookoutHeight * Math.sqrt(1 + rightTopChordPitch * rightTopChordPitch),
    );

    const topChordPlaneWithOffset = planeCopyMoveAlongNormal(
      bottomChordPlaneWithOffset,
      -0.5 * topChordHeight + 0.5 * bottomChordHeight,
    );
    const webChordPlaneWithOffset = planeCopyMoveAlongNormal(
      bottomChordPlaneWithOffset,
      -0.5 * webChordHeight + 0.5 * bottomChordHeight,
    );

    const {
      bottomChord: bottomChordTopPoints,
      leftHeelChord,
      rightHeelChord,
    } = generateBottomChord(
      leftSpan,
      rightSpan,
      leftOverhang,
      rightOverhang,
      leftTopChordPitch,
      rightTopChordPitch,
      topChordWidth,
      bottomChordWidth,
      bottomChordOffset,
    );

    const verticalWebChords = this.generateWebChords(
      leftTopChordPitch,
      rightTopChordPitch,
      bottomChordTopPoints,
      webChordHorizontalSpacing,
      webChordHalfWidth,
      unsignedNumberNew(lookoutHeight + topChordWidth / 2),
    );

    const [leftTopChordTopPointsToFramingPlane, rightTopChordTopPointsToFramingPlane] = projectChordsToFramingPlane(
      [left, right],
      topChordPlaneWithOffset,
    );

    const [bottomChordTopPointsToFramingPlane, leftHeelChordToFramingPlane, rightHeelChordToFramingPlane] =
      projectChordsToFramingPlane([bottomChordTopPoints], bottomChordPlaneWithOffset);

    const verticalWebChordsToFramingPlane = projectChordsToFramingPlane(
      [...verticalWebChords, leftHeelChord, rightHeelChord],
      webChordPlaneWithOffset,
    );

    const topChords = lumberPiecesByProfileNew(LumberProfileCatalog[topChordProfile], { [topChordProfile]: [] });
    const bottomChords = lumberPiecesByProfileNew(LumberProfileCatalog[bottomChordProfile], {
      [bottomChordProfile]: [],
    });
    const webChords = lumberPiecesByProfileNew(LumberProfileCatalog[webChordProfile], { [webChordProfile]: [] });

    /**
     * Using the plane normals derived from the top chord planes seems to be causing rendering instabilities. (clipRidgePlanes)
     * Everything renders correctly for slopes ≥ 1, but inconsistencies emerge for shallower slopes.
     * As a temporary workaround, I have forced the normal to use the negative Z unit vector,
     * which ensures consistent display across all slope values. This behavior needs to be investigated further
     * to understand the root cause. (@todo)
     */
    setupTopChordRails(
      topChords,
      leftTopChordTopPointsToFramingPlane ? polyLine3Reverse(leftTopChordTopPointsToFramingPlane) : null,
      leftEaveFasciaSize,
      0,
      0,
      -1,
    );

    setupTopChordRails(
      topChords,
      rightTopChordTopPointsToFramingPlane ? polyLine3Reverse(rightTopChordTopPointsToFramingPlane) : null,
      rightEaveFasciaSize,
      0,
      0,
      -1,
    );

    const { uX, uY, uZ, vX, vY, vZ } = bottomChordPlaneWithOffset;

    setupBottomChordRails(bottomChords, bottomChordTopPointsToFramingPlane, vX, vY, vZ);

    const allWebChords = [
      ...verticalWebChordsToFramingPlane,
      leftHeelChordToFramingPlane,
      rightHeelChordToFramingPlane,
    ];

    allWebChords.forEach((webChord) => {
      setupWebChordRails(webChords, webChord, uX, uY, uZ);
    });

    return {
      trussType: RoofTrussType.STICK_FRAME_TRUSS,
      components: [
        { type: RoofFramingComponentType.TOP_CHORD, lumber: topChords },
        { type: RoofFramingComponentType.BOTTOM_CHORD, lumber: bottomChords },
        { type: RoofFramingComponentType.WEB, lumber: webChords },
      ],
      framingType: RoofFramingType.TRUSS,
      plane: trussPlane,
    };
  }
}
